'use client'

import Link from 'next/link'
import { ArrowRight, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

interface MegaMenuProps {
  category: {
    name: string
    slug: string
    subcategories: Array<{
      id: number | string
      name: string
      slug: string
      productCount: number
    }>
    totalProducts: number
    icon?: string
  }
}

export function MegaMenu({ category }: MegaMenuProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 min-h-[350px]">
      {/* Category Overview */}
      <div className="lg:col-span-1 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-6 flex flex-col">
        <div className="mb-6 flex-1">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">{category.name}</h2>
          <p className="text-slate-600 text-sm leading-relaxed">
            Explore our comprehensive range of {category.name.toLowerCase()} components for all your project needs.
          </p>
          <div className="mt-4 flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {category.totalProducts.toLocaleString()} products
            </Badge>
          </div>
        </div>
        
        <Link 
          href={`/products?category=${category.slug}&page=1`}
          className="block"
        >
          <Button className="w-full bg-gradient-to-r from-[#518BC4] to-[#7C1414] hover:from-[#7C1414] hover:to-[#518BC4] text-white shadow-md transition-all duration-300">
            View All 
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>

      {/* Popular Subcategories (Top 5 by product count) */}
      <div className="lg:col-span-3">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Popular Categories</h3>
          <div className="w-16 h-1 bg-gradient-to-r from-[#518BC4] to-[#7C1414] rounded"></div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {category.subcategories.map((sub) => (
            <Link
              key={sub.id}
              href={`/products?category=${sub.slug}&page=1`}
              className="group flex items-center justify-between py-3 px-4 rounded-lg border border-slate-200 hover:border-[#518BC4] hover:bg-slate-50 transition-all duration-200 hover:shadow-md"
            >
              <div className="flex-1 min-w-0">
                <span className="text-slate-700 group-hover:text-[#518BC4] group-hover:font-medium transition-all text-sm block truncate">
                  {sub.name}
                </span>
                <span className="text-xs text-slate-500">
                  {sub.productCount.toLocaleString()} products
                </span>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#518BC4] transition-colors flex-shrink-0 ml-2" />
            </Link>
          ))}
        </div>
        
        {/* View More Link */}
        <div className="mt-6 text-center">
          <Link 
            href={`/products?category=${category.slug}&page=1`}
            className="inline-flex items-center text-sm text-slate-600 hover:text-[#518BC4] font-medium transition-colors"
          >
            Browse all {category.name.toLowerCase()} categories
            <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
      </div>
    </div>
  )
}
